/* eslint-disable complexity */
import {
	type ButtonHTMLAttributes,
	type MouseEvent,
	type ReactNode,
	type Ref,
	useId,
	useRef,
	useState,
} from "react";
import { Link, type LinkProps } from "react-router";

import { tv, type VariantProps } from "tailwind-variants";

import { useDisconnectedState } from "@virtual-brain-lib/frontend-design-system/components";
import {
	chain,
	cn,
	type VariantSlotsToClassNames,
} from "@virtual-brain-lib/frontend-design-system/utils";

import { Badge } from "./Badge";
import { Icon, type IconProps, type Icons } from "./Icon";
import { Span, type SpanProps } from "./Span";
import { Tooltip, type TooltipProps } from "./Tooltip";

export const buttonVariants = tv({
	slots: {
		wrapper:
			"group/button relative cursor-pointer border border-transparent transition-all duration-200 ease-in-out focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60 [&_span]:cursor-pointer",
		base: "relative flex w-full items-center justify-center overflow-hidden",
		text: "overflow-hidden leading-normal group-disabled/button:cursor-not-allowed",
		badge: "absolute",
	},
	variants: {
		lineClamp: {
			false: {
				text: "text-ellipsis whitespace-nowrap",
			},
		},
		size: {
			xs: {
				wrapper: "rounded-md",
				base: "h-4 min-h-4 gap-0.5 rounded-xl px-2 group-data-[is-icon-only=true]/button:min-w-4 group-data-[is-icon-only=true]/button:px-0",
				badge: "-top-1 -right-1",
			},
			sm: {
				wrapper: "rounded-md",
				base: "h-7 min-h-7 gap-1 rounded-xl px-2 group-data-[is-icon-only=true]/button:min-w-7 group-data-[is-icon-only=true]/button:px-0",
				badge: "-top-1.5 -right-1.5",
			},
			md: {
				wrapper: "rounded-lg",
				base: "h-8 min-h-8 gap-2 rounded-2xl px-2 group-data-[is-icon-only=true]/button:min-w-8 group-data-[is-icon-only=true]/button:px-0",
				badge: "-top-2 -right-2",
			},
			lg: {
				wrapper: "rounded-xl",
				base: "h-10 min-h-10 gap-2.5 rounded-2xl px-3 group-data-[is-icon-only=true]/button:min-w-10 group-data-[is-icon-only=true]/button:px-0",
				badge: "-top-2.5 -right-2.5",
			},
			xl: {
				wrapper: "rounded-2xl",
				base: "h-12 min-h-12 gap-3 rounded-3xl px-4 py-3 text-2xl group-data-[is-icon-only=true]/button:min-w-12 group-data-[is-icon-only=true]/button:px-0",
				badge: "-top-3 -right-3",
			},
		},
		color: {
			primary: {
				wrapper: "outline-primary-active-ring",
				ripple: "",
				count: "border border-primary-border bg-primary text-on-primary",
			},
			secondary: {},
			danger: {
				wrapper: "outline-danger-active-ring",
				ripple: "",
			},
			info: {
				wrapper: "outline-info-active-ring",
				ripple: "",
			},
			success: {
				wrapper: "outline-success-active-ring",
				ripple: "",
			},
			warning: {
				wrapper: "outline-warning-active-ring",
				ripple: "",
			},
			onGlobalBackground: {
				wrapper: "text-secondary",
				ripple: "",
			},
			cta: {
				wrapper: "text-on-cta",
			},
		},
		variant: {
			solid: {
				text: "font-medium",
				wrapper: "",
			},
			outline: {
				wrapper: "border bg-transparent",
			},
			ghost: {
				wrapper: "bg-transparent",
			},
			outlineWhite: {
				wrapper: "border bg-white",
				text: "text-black",
			},
			bordered: {
				text: "font-medium",
				wrapper: "border",
			},
		},
		textPosition: {
			start: {
				wrapper: "w-full",
				base: "justify-start",
				text: "text-start",
			},
			center: {
				base: "justify-center",
				text: "text-center",
			},
			end: {
				wrapper: "w-full",
				base: "justify-end",
				text: "text-end",
			},
		},
		isDisableHover: {
			true: {
				wrapper: "not-disabled:hover:bg-transparent! not-disabled:active:bg-transparent!",
			},
		},
		shape: {
			rectangle: {},
			pill: {
				wrapper: "rounded-full",
				base: "rounded-full",
			},
		},
	},
	compoundVariants: [
		{
			color: "primary",
			variant: "solid",
			className: {
				wrapper:
					"bg-primary text-on-primary not-disabled:hover:bg-primary-hover not-disabled:active:bg-primary-active",
				text: "text-on-primary [&_span]:text-on-primary",
			},
		},
		{
			color: "primary",
			variant: "outline",
			className: {
				wrapper:
					"border-primary-border text-on-primary-2 not-disabled:hover:bg-primary-hover-2 not-disabled:active:bg-primary-active-2",
				text: "text-on-primary-2 [&_span]:text-on-primary-2",
			},
		},
		{
			color: "primary",
			variant: "ghost",
			className: {
				wrapper:
					"text-on-primary-2 not-disabled:hover:bg-primary-hover-2 not-disabled:active:bg-primary-active-2",
				text: "text-on-primary-2 [&_span]:text-on-primary-2",
			},
		},
		{
			color: "primary",
			variant: "outlineWhite",
			className: {
				wrapper:
					"border-primary-border text-on-primary not-disabled:hover:bg-primary-hover-2 not-disabled:active:bg-primary-active-2",
				text: "text-on-primary [&_span]:text-on-primary",
			},
		},
		{
			color: "primary",
			variant: "bordered",
			className: {
				wrapper:
					"border-primary-border bg-primary text-on-primary not-disabled:hover:bg-primary-hover not-disabled:active:bg-primary-active",
				text: "text-on-primary [&_span]:text-on-primary",
			},
		},
		{
			color: "secondary",
			variant: "solid",
			className: {
				wrapper:
					"bg-secondary text-on-secondary not-disabled:hover:bg-secondary-hover not-disabled:active:bg-secondary-active",
				text: "font-semibold text-on-secondary [&_span]:text-on-secondary",
			},
		},
		{
			color: "secondary",
			variant: "outlineWhite",
			className: {
				wrapper:
					"border-secondary-border text-on-secondary not-disabled:hover:bg-secondary-hover not-disabled:active:bg-secondary-active",
				text: "font-semibold text-on-secondary [&_span]:text-on-secondary",
			},
		},
		{
			color: "secondary",
			variant: "bordered",
			className: {
				wrapper:
					"border-secondary-border bg-secondary text-on-secondary not-disabled:hover:bg-secondary-hover not-disabled:active:bg-secondary-active",
				text: "font-semibold text-on-secondary [&_span]:text-on-secondary",
			},
		},
		{
			color: "danger",
			variant: "solid",
			className: {
				wrapper:
					"bg-danger text-on-danger not-disabled:hover:bg-danger-hover not-disabled:active:bg-danger-active",
				text: "text-on-danger [&_span]:text-on-danger",
			},
		},
		{
			color: "danger",
			variant: "outline",
			className: {
				wrapper:
					"border-danger-border text-on-danger-2 not-disabled:hover:bg-danger-hover-2 not-disabled:active:bg-danger-active-2",
				text: "text-on-danger-2 [&_span]:text-on-danger-2",
			},
		},
		{
			color: "danger",
			variant: "ghost",
			className: {
				wrapper:
					"text-on-danger-2 not-disabled:hover:bg-danger-hover-2 not-disabled:active:bg-danger-active-2",
				text: "text-on-danger-2 [&_span]:text-on-danger-2",
			},
		},
		{
			color: "info",
			variant: "solid",
			className: {
				wrapper:
					"bg-info text-on-info not-disabled:hover:bg-info-hover not-disabled:active:bg-info-active",
				text: "text-on-info [&_span]:text-on-info",
			},
		},
		{
			color: "info",
			variant: "outline",
			className: {
				wrapper:
					"border-info-border text-on-info-2 not-disabled:hover:bg-info-hover-2 not-disabled:active:bg-info-active-2",
				text: "text-on-info-2 [&_span]:text-on-info-2",
			},
		},
		{
			color: "info",
			variant: "ghost",
			className: {
				wrapper:
					"text-on-info-2 not-disabled:hover:bg-info-hover-2 not-disabled:active:bg-info-active-2",
				text: "text-on-info-2 [&_span]:text-on-info-2",
			},
		},
		{
			color: "success",
			variant: "solid",
			className: {
				wrapper:
					"bg-success text-on-success not-disabled:hover:bg-success-hover not-disabled:active:bg-success-active",
				text: "text-on-success [&_span]:text-on-success",
			},
		},
		{
			color: "success",
			variant: "outline",
			className: {
				wrapper:
					"border-success-border text-on-success-2 not-disabled:hover:bg-success-hover-2 not-disabled:active:bg-success-active-2",
				text: "text-on-success-2 [&_span]:text-on-success-2",
			},
		},
		{
			color: "success",
			variant: "ghost",
			className: {
				wrapper:
					"text-on-success-2 not-disabled:hover:bg-success-hover-2 not-disabled:active:bg-success-active-2",
				text: "text-on-success-2 [&_span]:text-on-success-2",
			},
		},
		{
			color: "warning",
			variant: "solid",
			className: {
				wrapper:
					"bg-warning text-on-warning not-disabled:hover:bg-warning-hover not-disabled:active:bg-warning-active",
				text: "text-on-warning [&_span]:text-on-warning",
			},
		},
		{
			color: "warning",
			variant: "outline",
			className: {
				wrapper:
					"border-warning-border text-on-warning-2 not-disabled:hover:bg-warning-hover-2 not-disabled:active:bg-warning-active-2",
				text: "text-on-warning-2 [&_span]:text-on-warning-2",
			},
		},
		{
			color: "warning",
			variant: "ghost",
			className: {
				wrapper:
					"text-on-warning-2 not-disabled:hover:bg-warning-hover-2 not-disabled:active:bg-warning-active-2",
				text: "text-on-warning-2 [&_span]:text-on-warning-2",
			},
		},
		{
			color: "onGlobalBackground",
			variant: "solid",
			className: {
				wrapper: "rounded-full bg-secondary text-on-secondary",
				text: "text-on-secondary [&_span]:text-on-secondary",
			},
		},
		{
			color: "onGlobalBackground",
			variant: "ghost",
			className: {
				wrapper: "rounded-full text-secondary active:bg-secondary active:text-on-secondary",
				text: "text-secondary [&_span]:text-secondary",
			},
		},
		{
			color: "cta",
			variant: "bordered",
			className: {
				wrapper:
					"rounded-full border-[1.5px] border-cta-border bg-cta not-disabled:hover:border-cta-border-action not-disabled:active:border-cta-border-action not-disabled:active:bg-cta-active",
				text: "text-on-cta",
			},
		},
	],
	defaultVariants: {
		size: "md",
		shape: "rectangle",
	},
});

export type ButtonVariant = Omit<VariantProps<typeof buttonVariants>, "lineClamp">;

export type ButtonProps = ButtonVariant &
	ButtonHTMLAttributes<HTMLButtonElement> & {
		readonly leftIcon?: Icons;
		readonly leftComponent?: ReactNode;
		readonly rightIcon?: Icons;
		readonly rightComponent?: ReactNode;
		readonly ref?: Ref<HTMLButtonElement>;
		readonly count?: number;
		readonly classNames?: VariantSlotsToClassNames<typeof buttonVariants>;
		readonly tooltip?: Omit<TooltipProps, "anchor">;
		readonly isStopPropagation?: boolean;
		readonly isDisableHover?: boolean;
		readonly linkProps?: Omit<LinkProps, "className">;
	};

const textVariant: Record<NonNullable<ButtonProps["size"]>, NonNullable<SpanProps["variant"]>> = {
	xs: "button-xs",
	sm: "button-sm",
	md: "button-md",
	lg: "button-lg",
	xl: "button-xl",
};

const iconSize: Record<NonNullable<ButtonProps["size"]>, NonNullable<IconProps["size"]>> = {
	xs: "sm",
	sm: "md",
	md: "lg",
	lg: "xl",
	xl: "2xl",
};

export function Button({
	leftIcon,
	rightIcon,
	children,
	size = "md",
	className,
	color = "primary",
	variant = "solid",
	ref,
	onClick,
	onMouseEnter,
	count = 0,
	classNames,
	tooltip: tooltipProps,
	disabled,
	textPosition = "center",
	leftComponent,
	rightComponent,
	isStopPropagation = true,
	isDisableHover = false,
	linkProps,
	type = "button",
	shape = "rectangle",
	...props
}: ButtonProps) {
	const tooltipId = useId();

	const [isTextTruncated, setIsTextTruncated] = useState(false);
	const textRef = useRef<HTMLSpanElement>(null);
	const { isDisconnected } = useDisconnectedState();

	const checkTruncation = () => {
		if (textRef.current) {
			const isTruncated = textRef.current.scrollWidth > textRef.current.clientWidth;

			setIsTextTruncated(isTruncated);
		}
	};

	const tooltip =
		isTextTruncated && (!tooltipProps || tooltipProps?.hidden)
			? { children, className: "break-all" }
			: tooltipProps;

	const {
		wrapper,
		base,
		badge: badgeClassName,
		text: textClassName,
	} = buttonVariants({
		color,
		variant,
		size,
		shape,
		textPosition,
		isDisableHover,
		lineClamp: classNames?.text?.includes("line-clamp"),
		...props,
	});

	function stopPropagation(event: MouseEvent<HTMLButtonElement>) {
		if (isStopPropagation) {
			event.stopPropagation();
		}
	}

	function preventDefault(event: MouseEvent<HTMLButtonElement>) {
		if (type === "button" && !linkProps) {
			event.preventDefault();
		}
	}

	const content = (
		<>
			{leftComponent && leftComponent}
			{leftIcon && <Icon name={leftIcon} size={iconSize[size]} />}
			{children && (
				<Span
					ref={textRef}
					className={textClassName({ className: classNames?.text })}
					variant={textVariant[size]}
				>
					{children}
				</Span>
			)}
			{rightIcon && <Icon name={rightIcon} size={iconSize[size]} />}
			{rightComponent && rightComponent}
		</>
	);

	const baseClassName = base({ className: classNames?.base });

	return (
		<>
			<button
				ref={ref}
				type={type}
				data-is-icon-only={!children}
				data-size={size}
				className={wrapper({
					className: cn(className, classNames?.wrapper),
				})}
				data-tooltip={tooltipId}
				disabled={isDisconnected || disabled}
				onClick={chain(preventDefault, onClick, stopPropagation)}
				onMouseEnter={chain(onMouseEnter, checkTruncation)}
				{...props}
			>
				{linkProps ? (
					<Link {...linkProps} className={baseClassName}>
						{content}
					</Link>
				) : (
					<div className={baseClassName}>{content}</div>
				)}
				{count > 0 && (
					<Badge
						className={badgeClassName({ className: classNames?.badge })}
						color={color}
						size={size}
						count={count}
					/>
				)}
			</button>
			{tooltip && <Tooltip anchor={tooltipId} {...tooltip} />}
		</>
	);
}
